
import { useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { PrayerForm, type WalkthroughState } from '@/components/prayer';
import { cn } from '@/lib/utils';

function getHeaderCopy(state: WalkthroughState): { title: string; subtitle?: string } {
  switch (state.step) {
    case 1:
      return { title: 'How would you like to share today?' };
    case 2:
      return state.type === 'praise_report'
        ? {
            title: 'Share what God has done',
            subtitle: 'A few sentences is perfect — your story encourages others.',
          }
        : {
            title: "What's on your heart?",
            subtitle: 'A few sentences is perfect — others may pray with you.',
          };
    case 3:
      return {
        title: 'How can we reach you?',
        subtitle: 'For personal follow-up only. Never shown publicly.',
      };
    default:
      return { title: 'Share Your Prayer' };
  }
}

export default function SubmitPage() {
  const [searchParams] = useSearchParams();
  const raw = searchParams.get('type') ?? undefined;
  const initialType =
    raw === 'answered_prayer' || raw === 'praise_report'
      ? ('praise_report' as const)
      : raw === 'prayer_request'
        ? ('prayer_request' as const)
        : undefined;

  const initialWalkthroughState: WalkthroughState = {
    step: initialType ? 2 : 1,
    type: initialType === 'praise_report' ? 'praise_report' : 'prayer_request',
  };

  const [walkthroughState, setWalkthroughState] = useState<WalkthroughState>(initialWalkthroughState);
  const [showFooter, setShowFooter] = useState(false);

  const handleWalkthroughStateChange = useCallback((state: WalkthroughState) => {
    setWalkthroughState(state);
    if (state.step === 1) setShowFooter(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setShowFooter(true);
  }, []);

  const header = useMemo(() => getHeaderCopy(walkthroughState), [walkthroughState]);

  const isChooseStep = walkthroughState.step === 1;

  return (
    <main
      className={cn(
        'relative min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800',
        isChooseStep && 'flex flex-col items-center justify-center'
      )}
    >
      {isChooseStep && (
        <Link
          to="/"
          className="absolute top-6 left-6 z-10 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
        >
          <span aria-hidden>←</span> Home
        </Link>
      )}

      <div
        className={cn(
          'container mx-auto px-4 py-10 w-full',
          isChooseStep ? 'max-w-2xl flex flex-col' : 'max-w-lg'
        )}
      >

        <header className="text-center mb-10">
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800 dark:text-slate-100 mb-2 tracking-tight">
            {header.title}
          </h1>
          {header.subtitle && (
            <p className="text-slate-500 dark:text-slate-400 text-sm italic">{header.subtitle}</p>
          )}
        </header>

        <PrayerForm
          initialType={initialType}
          onWalkthroughStateChange={handleWalkthroughStateChange}
          onSuccess={handleSuccess}
        />

        {showFooter && (
          <footer className="mt-12 pt-6 border-t border-slate-200/60 dark:border-slate-700/60">
            <p className="text-center text-sm text-slate-500 dark:text-slate-400">
              Your prayers are important to us and will be handled with care.
            </p>
          </footer>
        )}
      </div>
    </main>
  );
}
